/**
 * Configuración de i18n.
 *
 * Toda cadena visible pasa por aquí (principio V): el proyector del aula y el móvil del alumno
 * pueden estar en idiomas distintos, y deben poder estarlo.
 *
 * Los tres idiomas se importan de forma estática y viajan en el paquete. Con tres idiomas y una
 * aplicación de este tamaño, la carga perezosa solo añadiría estados de carga que gestionar.
 */

import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { FALLBACK_LOCALE, LOCALES, type Locale } from '@clica/shared';
import ca from './locales/ca/translation.json';
import en from './locales/en/translation.json';
import es from './locales/es/translation.json';

export const resources = {
  es: { translation: es },
  ca: { translation: ca },
  en: { translation: en },
} as const;

/** Comprueba que una cadena cualquiera es uno de los idiomas de la plataforma. */
export function isLocale(candidate: string | undefined): candidate is Locale {
  return candidate !== undefined && (LOCALES as readonly string[]).includes(candidate);
}

/**
 * Idioma que prefiere el navegador, si es uno de los nuestros.
 *
 * Se prueba primero la etiqueta completa (`ca-ES`) reducida a su parte de idioma, porque eso es
 * lo que suele traer un móvil configurado en catalán.
 */
export function detectBrowserLocale(
  languages: readonly string[] = navigator.languages,
): Locale | undefined {
  for (const tag of languages) {
    const base = tag.split('-')[0]?.toLowerCase();
    if (isLocale(base)) return base;
  }
  return undefined;
}

export async function initI18n(initialLocale?: Locale): Promise<void> {
  const locale = initialLocale ?? detectBrowserLocale() ?? FALLBACK_LOCALE;

  await i18next.use(initReactI18next).init({
    resources,
    lng: locale,
    fallbackLng: FALLBACK_LOCALE,
    supportedLngs: LOCALES,
    interpolation: {
      // React ya escapa todo lo que pinta; volver a escapar aquí duplicaría las entidades.
      escapeValue: false,
    },
    // Una clave inexistente debe reventar en desarrollo y en las pruebas, no pasar inadvertida
    // como texto en bruto en la pantalla del aula.
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: (_lngs, _ns, key) => {
      if (import.meta.env.DEV) throw new Error(`Falta la clave de traducción «${key}»`);
    },
  });

  // El `lang` del documento importa para los lectores de pantalla y para la separación silábica
  // del navegador. El `index.html` trae un valor fijo, así que hay que corregirlo aquí: sin esto,
  // una interfaz en catalán se anunciaría como castellana.
  document.documentElement.lang = locale;
}

/** Cambia el idioma de la interfaz. La preferencia se guarda en el perfil, no aquí. */
export async function applyLocale(locale: Locale): Promise<void> {
  await i18next.changeLanguage(locale);
  document.documentElement.lang = locale;
}

export { i18next };
